import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';
import * as XLSX from 'xlsx';

function PrePurchaseList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    product: '',
    quantity: 1,
    buyer: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const itemsRef = collection(db, 'pre-purchases');
      const q = query(itemsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const itemList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setItems(itemList);
    } catch (error) {
      console.error('선구매 리스트 불러오기 오류:', error);
      alert('선구매 리스트를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!formData.product.trim()) {
      alert('제품명을 입력하세요.');
      return;
    }
    if (!formData.buyer.trim()) {
      alert('구매자를 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'pre-purchases'), {
        ...formData,
        quantity: Number(formData.quantity) || 1,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      alert('✅ 선구매 항목이 추가되었습니다.');
      setShowAddModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('선구매 항목 추가 오류:', error);
      alert('선구매 항목 추가 중 오류가 발생했습니다.');
    }
  };

  const handleUpdateItem = async () => {
    if (!formData.product.trim()) {
      alert('제품명을 입력하세요.');
      return;
    }
    if (!formData.buyer.trim()) {
      alert('구매자를 입력하세요.');
      return;
    }

    try {
      const itemRef = doc(db, 'pre-purchases', editingItem.id);
      await updateDoc(itemRef, {
        ...formData,
        quantity: Number(formData.quantity) || 1,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      alert('✅ 선구매 항목이 수정되었습니다.');
      setEditingItem(null);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('선구매 항목 수정 오류:', error);
      alert('선구매 항목 수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteItem = async (itemId, productName) => {
    if (!window.confirm(`"${productName}" 항목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'pre-purchases', itemId));
      alert('✅ 선구매 항목이 삭제되었습니다.');
      fetchItems();
    } catch (error) {
      console.error('선구매 항목 삭제 오류:', error);
      alert('선구매 항목 삭제 중 오류가 발생했습니다.');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      product: item.product,
      quantity: item.quantity || 1,
      buyer: item.buyer || ''
    });
  };

  const resetForm = () => {
    setFormData({
      product: '',
      quantity: 1,
      buyer: ''
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingItem(null);
    resetForm();
  };

  // 유연한 컬럼 값 추출 함수
  const getColumnValue = (row, possibleNames) => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return row[name];
      }
    }
    return null;
  };

  // 엑셀 임포트
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportProgress({ current: 0, total: 0 });

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });

          // "선구매 리스트" 시트 찾기 또는 첫 번째 시트 사용
          let sheetName = workbook.SheetNames.find(name => name.includes('선구매 리스트'));
          if (!sheetName) sheetName = workbook.SheetNames[0];

          const worksheet = workbook.Sheets[sheetName];
          // 첫 번째 행이 제목일 수 있으므로 range: 1로 두 번째 행부터 시작
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', range: 1 });

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          // 첫 번째 행이 헤더인지 확인하고 필터링
          const filteredData = jsonData.filter(row => {
            const product = getColumnValue(row, ['제품', '선구매 리스트', '제품명', 'Product']);
            return product && product !== '제품' && product !== '선구매 리스트';
          });

          setImportProgress({ current: 0, total: filteredData.length });

          for (let i = 0; i < filteredData.length; i++) {
            const row = filteredData[i];

            try {
              const product = getColumnValue(row, ['제품', '선구매 리스트', '제품명', 'Product']);
              const quantity = getColumnValue(row, ['수량', 'Quantity', '개수']);
              const buyer = getColumnValue(row, ['구매자', 'Buyer', '고객', '이름']);

              if (!product) {
                errors.push(`행 ${i + 2}: 제품명이 누락되었습니다.`);
                errorCount++;
                continue;
              }

              await addDoc(collection(db, 'pre-purchases'), {
                product: String(product).trim(),
                quantity: parseInt(quantity) || 1,
                buyer: buyer ? String(buyer).trim() : '',
                createdAt: serverTimestamp(),
                createdBy: user.email,
                importedAt: serverTimestamp()
              });

              successCount++;
              setImportProgress({ current: i + 1, total: filteredData.length });

              // 배치마다 잠깐 대기
              if ((i + 1) % 20 === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }

            } catch (error) {
              errors.push(`행 ${i + 2}: ${error.message}`);
              errorCount++;
            }
          }

          if (successCount > 0) {
            alert(`✅ ${successCount}건의 데이터가 성공적으로 가져와졌습니다!${errorCount > 0 ? `\n❌ ${errorCount}건 실패` : ''}`);
            fetchItems();
          } else if (errorCount > 0) {
            alert(`❌ 가져오기 실패: ${errors.slice(0, 5).join('\n')}`);
          } else {
            alert('가져올 데이터가 없습니다.');
          }

        } catch (error) {
          console.error('파일 파싱 오류:', error);
          alert('엑셀 파일 파싱 중 오류가 발생했습니다.');
        } finally {
          setImporting(false);
          setImportProgress({ current: 0, total: 0 });
        }
      };
      reader.readAsBinaryString(file);

    } catch (error) {
      console.error('엑셀 가져오기 오류:', error);
      alert('엑셀 가져오기 중 오류가 발생했습니다.');
      setImporting(false);
    }

    e.target.value = '';
  };

  const filteredItems = items.filter(item =>
    item.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.buyer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalQuantity = filteredItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">선구매 리스트</h2>
          <p className="text-gray-500 mt-1">선구매 요청 제품을 관리합니다.</p>
        </div>
        <div className="flex gap-3">
          <label className={`px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-medium flex items-center cursor-pointer ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <Icons.Upload className="w-5 h-5 mr-2" />
            엑셀 임포트
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              disabled={importing}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold flex items-center shadow-md"
          >
            <Icons.Plus className="w-5 h-5 mr-2" />
            선구매 추가
          </button>
        </div>
      </div>

      {/* 임포트 진행 상태 */}
      {importing && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
            <span className="font-bold text-blue-900">엑셀 데이터를 가져오는 중...</span>
          </div>
          {importProgress.total > 0 && (
            <div className="mt-2">
              <div className="flex justify-between text-sm text-blue-700 mb-1">
                <span>진행률</span>
                <span>{importProgress.current} / {importProgress.total}</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Icons.List className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 항목</p>
              <p className="text-2xl font-bold text-gray-900">{filteredItems.length}건</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Icons.Box className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 수량</p>
              <p className="text-2xl font-bold text-gray-900">{totalQuantity}개</p>
            </div>
          </div>
        </div>
      </div>

      {/* 검색 */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-brand-100 p-4">
        <div className="relative">
          <Icons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="제품명, 구매자 검색"
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* 목록 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">선구매 리스트를 불러오는 중...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Icons.ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            {searchTerm ? '검색 결과가 없습니다.' : '등록된 선구매 항목이 없습니다.'}
          </p>
          {!searchTerm && (
            <div className="flex justify-center gap-3">
              <label className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold cursor-pointer flex items-center">
                <Icons.Upload className="w-5 h-5 mr-2" />
                엑셀로 가져오기
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  disabled={importing}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                직접 추가하기
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-brand-50 border-b border-brand-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">제품</th>
                <th className="px-6 py-4 text-center text-sm font-bold text-brand-900">수량</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">구매자</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-brand-900">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Icons.ShoppingBag className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="font-bold text-gray-900">{item.product}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                      {item.quantity || 1}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Icons.User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-700">{item.buyer || '-'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => startEdit(item)}
                        className="px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition text-sm font-medium"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id, item.product)}
                        className="px-3 py-1.5 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-300 text-gray-700 hover:text-red-600 rounded-lg transition text-sm font-medium"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editingItem) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingItem ? '선구매 항목 수정' : '선구매 항목 추가'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  제품 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  placeholder="제품명을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  수량 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  구매자 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.buyer}
                  onChange={(e) => setFormData({ ...formData, buyer: e.target.value })}
                  placeholder="구매자 이름을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold"
              >
                취소
              </button>
              <button
                onClick={editingItem ? handleUpdateItem : handleAddItem}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                {editingItem ? '수정' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrePurchaseList;
